import React, { createContext, useContext, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const CustomDialogContext = createContext();

export const CustomDialogProvider = ({ children }) => {
  const [dialogState, setDialogState] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'info', // 'info', 'success', 'warning', 'error', 'confirm'
    confirmText: 'OK',
    cancelText: 'Cancel',
    resolvePromise: null
  });

  const location = useLocation();

  useEffect(() => {
    // Automatically close and resolve false on navigation/route change to prevent stuck overlays
    if (dialogState.isOpen && dialogState.resolvePromise) {
      dialogState.resolvePromise(false);
    }
    setDialogState(prev => ({ ...prev, isOpen: false }));
  }, [location.pathname]);

  const showAlert = (optionsOrTitle, message = '', type = 'info', confirmText = 'OK') => {
    let title = optionsOrTitle;
    if (typeof optionsOrTitle === 'object' && optionsOrTitle !== null) {
      title = optionsOrTitle.title || 'Alert';
      message = optionsOrTitle.message || '';
      type = optionsOrTitle.type || 'info';
      confirmText = optionsOrTitle.confirmText || 'OK';
    }
    return new Promise((resolve) => {
      setDialogState({
        isOpen: true,
        title,
        message,
        type,
        confirmText,
        cancelText: '',
        resolvePromise: resolve
      });
    });
  };

  const showConfirm = (optionsOrTitle, message = '', confirmText = 'Confirm', cancelText = 'Cancel', type = 'confirm') => {
    let title = optionsOrTitle;
    if (typeof optionsOrTitle === 'object' && optionsOrTitle !== null) {
      title = optionsOrTitle.title || 'Confirm';
      message = optionsOrTitle.message || '';
      confirmText = optionsOrTitle.confirmText || 'Confirm';
      cancelText = optionsOrTitle.cancelText || 'Cancel';
      type = optionsOrTitle.type || 'confirm';
    }
    return new Promise((resolve) => {
      setDialogState({
        isOpen: true,
        title,
        message,
        type,
        confirmText,
        cancelText,
        resolvePromise: resolve
      });
    });
  };

  const handleConfirm = () => {
    if (dialogState.resolvePromise) {
      dialogState.resolvePromise(true);
    }
    setDialogState(prev => ({ ...prev, isOpen: false }));
  };

  const handleCancel = () => {
    if (dialogState.resolvePromise) {
      dialogState.resolvePromise(false);
    }
    setDialogState(prev => ({ ...prev, isOpen: false }));
  };

  const handleClose = () => {
    if (dialogState.type === 'confirm' || dialogState.type === 'warning') {
      handleCancel();
    } else {
      handleConfirm();
    }
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && dialogState.isOpen) {
        handleClose();
      }
    };
    if (dialogState.isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [dialogState.isOpen]);

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  return (
    <CustomDialogContext.Provider value={{ showAlert, showConfirm }}>
      {children}
      {dialogState.isOpen && (
        <div className="custom-modal-overlay active" onClick={handleOverlayClick}>
          <div className="custom-modal-box" onClick={(e) => e.stopPropagation()}>
            <div className="custom-modal-header">
              <h3 className="custom-modal-title">
                {dialogState.type === 'warning' && <span style={{ color: '#D28B47', marginRight: '4px' }}>⚠️</span>}
                {dialogState.type === 'error' && <span style={{ color: '#C53030', marginRight: '4px' }}>❌</span>}
                {dialogState.type === 'success' && <span style={{ color: '#38A169', marginRight: '4px' }}>✅</span>}
                {dialogState.title}
              </h3>
              <button className="custom-modal-close" onClick={handleClose} type="button" aria-label="Close dialog">&times;</button>
            </div>
            
            <div className="custom-modal-body">
              {dialogState.message}
            </div>

            <div className="custom-modal-actions">
              {(dialogState.type === 'confirm' || dialogState.cancelText) && (
                <button 
                  type="button" 
                  className="custom-modal-btn custom-modal-btn-cancel" 
                  onClick={handleCancel}
                >
                  {dialogState.cancelText || 'Cancel'}
                </button>
              )}
              <button 
                type="button" 
                className={`custom-modal-btn custom-modal-btn-confirm ${dialogState.type}`} 
                onClick={handleConfirm}
              >
                {dialogState.confirmText || (dialogState.type === 'confirm' ? 'Confirm' : 'OK')}
              </button>
            </div>
          </div>
        </div>
      )}
    </CustomDialogContext.Provider>
  );
};

export const useNotification = () => {
  const context = useContext(CustomDialogContext);
  if (!context) {
    throw new Error('useNotification must be used within a CustomDialogProvider');
  }
  return context;
};

export const useCustomDialog = useNotification;
